import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import Image from 'next/image';

import { Stack, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';

import { Button } from '@/components/Button/Button';
import discoverService from '@/services/discoverService';
import AuthModalStore from '@/stores/AuthModalStore';
import AuthStore from '@/stores/AuthStore';
import { loggedActionsGuard } from '@/utils/loggedActionsGuard';

import { Image as ImageType } from '@/types/Content/Image';
import { ICmsUser } from '@/types/Content/User';

import { ImageContainer, GroupCardWrapper } from './GroupCard.styles';
import Avatars from '../Avatars';

type GroupCardProps = {
  slug: string;
  groupId: string;
  groupName: string;
  totalUsers?: number;
  className?: string;
  users?: Array<ICmsUser>;
  groupImage?: ImageType;
  groupDescription: string;
};

const GroupCard: React.FC<GroupCardProps> = ({
  slug,
  users,
  groupId,
  groupName,
  className,
  groupImage,
  groupDescription,
  totalUsers,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { isLoggedIn, cmsUser } = AuthStore;

  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [userInfo, setUserInfo] = useState({} as any);

  useEffect(() => {
    if (userInfo && !userInfo.id) {
      setUserInfo(cmsUser);
    }
  }, [cmsUser, userInfo]);

  function handleOnClickToLearnMoreGroup() {
    if (!isLoggedIn) {
      AuthModalStore.showModal('signup');

      return;
    }

    router.push(`/discover/${slug}`);
  }

  const handleOnClickLeaveGroup = (loggedActionsGuard(
    isLoggedIn,
    false,
    async (e: Event) => {
      e.stopPropagation();

      setIsLoadingButton(true);
      const responseFromLeaveGroup = await discoverService.leaveGroup({
        groupId,
        userId: userInfo.id,
      });

      if (responseFromLeaveGroup) {
        setUserInfo((oldUserInfo) => {
          const newSubscriptionGroups = oldUserInfo.subscriptionGroups.filter(
            (currentGroup) => currentGroup.id !== groupId,
          );
          return { ...oldUserInfo, subscriptionGroups: newSubscriptionGroups };
        });

        await AuthStore.getCmsUser();
      }

      setIsLoadingButton(false);
    },
  ) as unknown) as MouseEventHandler;

  const handleOnClickJoinGroup = (loggedActionsGuard(
    isLoggedIn,
    false,
    async (e: Event) => {
      e.stopPropagation();

      if (!isLoggedIn) {
        AuthModalStore.showModal('signup');

        return;
      }

      setIsLoadingButton(true);
      const responseFromJoinGroup = await discoverService.joinGroup({
        groupId,
        userId: userInfo.id,
      });

      if (responseFromJoinGroup) {
        await AuthStore.getCmsUser();

        router.push(`/discover/${slug}`);
      }

      setIsLoadingButton(false);
    },
  ) as unknown) as MouseEventHandler;

  const isUserMember = useMemo(() => {
    if (!isLoggedIn || !(userInfo && userInfo.subscriptionGroups)) return false;

    return userInfo.subscriptionGroups.some(
      (currentGroup) => currentGroup.id === groupId,
    );
  }, [groupId, isLoggedIn, userInfo]);

  const usersAvatars = useMemo(
    () =>
      users &&
      users.slice(0, 5).map((currentUser) => ({
        id: currentUser?.id,
        name: currentUser.firstName,
        image: currentUser?.image,
      })),
    [users],
  );

  const textColor = isUserMember
    ? theme.palette.primary[900]
    : theme.palette.text.primary;

  return (
    <GroupCardWrapper
      active={isUserMember}
      onClick={handleOnClickToLearnMoreGroup}
      flexDirection="column"
    >
      <Stack
        direction="row"
        flexWrap="nowrap"
        alignItems="center"
        spacing={theme.spacing(4)}
      >
        {groupImage?.url && (
          <ImageContainer>
            <Image src={groupImage.url} layout="fill" objectFit="contain" />
          </ImageContainer>
        )}

        <Box flex={1}>
          <Typography
            variant="h4"
            fontSize={theme.spacing(5.75)}
            lineHeight={1.3}
            color={textColor}
          >
            {groupName}
          </Typography>
        </Box>
      </Stack>
      <Typography
        variant="body2"
        fontSize={theme.spacing(4.5)}
        lineHeight={1.4}
        color={textColor}
        mt={theme.spacing(2)}
        pl={theme.spacing(2)}
      >
        {groupDescription}
      </Typography>

      <div style={{ flex: 1 }} />

      <Box mt={theme.spacing(4)}>
        <Avatars users={usersAvatars} size={{ desktop: 7.5, mobile: 7.5 }} />
      </Box>
      <Stack
        mt={theme.spacing(4)}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        spacing={theme.spacing(4)}
      >
        {totalUsers ? (
          <Typography
            variant="body2"
            fontSize={theme.spacing(3.5)}
            lineHeight={1.4}
            color={
              isUserMember
                ? theme.palette.primary.main
                : theme.palette.grey[500]
            }
          >
            {totalUsers} follower{totalUsers > 1 ? 's' : ''}
          </Typography>
        ) : null}
        {isUserMember ? (
          <Button
            variant="outlined"
            loading={isLoadingButton}
            onClick={handleOnClickLeaveGroup}
            sx={{ color: theme.palette.primary[600] }}
          >
            Leave Group
          </Button>
        ) : (
          <Button loading={isLoadingButton} onClick={handleOnClickJoinGroup}>
            Join Group
          </Button>
        )}
      </Stack>

      {/* <div>
          <ButtonsWrapper>
            <Button variant="outlined" onClick={handleOnClickToLearnMoreGroup}>
              View All
            </Button>
          </ButtonsWrapper>
        </div> */}
    </GroupCardWrapper>
  );
};

GroupCard.defaultProps = {
  users: null,
  className: '',
  groupImage: null,
};

export default observer(GroupCard);
